/**
 * Agent kill switch: blocks all of an agent's tool calls at IAP verify,
 * timed or indefinite, with a live countdown and a lift action.
 *
 * Two parts, designed together (playground rounds 1-3):
 *
 *  - `AgentKillSwitchTrigger`, the commit control. Quiet at rest (muted red,
 *    meant to sit at the separated far edge of a header action row), it opens
 *    a popover with a duration choice and a HOLD-TO-KILL button: press and
 *    hold ~1.5s while the button fills; releasing early cancels. "Until
 *    lifted" takes a longer hold. The friction is physical, so no modal is
 *    needed, and kills stay reversible (lift exists) so no typed confirm.
 *
 *  - `AgentKillBanner`, the killed state. Renders nothing while no kill is
 *    live; otherwise a banner meant to sit directly under the detail header,
 *    visible on every tab: countdown (or "until lifted"), who/why when known,
 *    and the lift action. If the kill status query fails it says so instead
 *    of silently reading as "not killed".
 *
 * Data comes from @shared/data/agent-kills (org-scoped; fast poll only while
 * a kill is live). When a countdown reaches zero the banner invalidates the
 * kills query so the UI reconciles with the backend's auto-expiry within a
 * refetch, not a poll.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IconZap, IconClock, IconShieldCheck, IconAlert } from '@shared/icons';
import { useCurrentOrgId } from '@shared/data/use-current-org';
import {
  useAgentKills,
  useKillAgent,
  useUnkillAgent,
  killKeys,
  type AgentKill,
} from '@shared/data/agent-kills';
import { Button } from '../Button';
import { Popover } from '../Popover';
import { SegmentedControl } from '../SegmentedControl';

/* ── Duration model ──────────────────────────────────────────────────────── */

const DURATIONS = [
  { key: '60', label: '1 min', seconds: 60 },
  { key: '300', label: '5 min', seconds: 300 },
  { key: '1800', label: '30 min', seconds: 1800 },
  { key: '3600', label: '1 hour', seconds: 3600 },
  { key: 'inf', label: 'Until lifted', seconds: undefined },
] as const satisfies ReadonlyArray<{ key: string; label: string; seconds?: number }>;

type DurationKey = (typeof DURATIONS)[number]['key'];

const SEG_OPTIONS = DURATIONS.map((d) => ({ value: d.key, label: d.label }));

/** Closed lookup so a bad key fails at the type level, not as a silent 5 min. */
const DURATION_BY_KEY = Object.fromEntries(DURATIONS.map((d) => [d.key, d])) as Record<
  DurationKey,
  (typeof DURATIONS)[number]
>;

/** Base hold length; "until lifted" holds 1.75x longer, so friction scales. */
const HOLD_MS = 1500;
const INDEFINITE_HOLD_MS = Math.round(HOLD_MS * 1.75);

/* ── Shared derivation: the active kill for one agent ────────────────────── */

/**
 * The clients hardcode these when no operator reason exists (see
 * @shared/data/agent-kills killAgent and the graph slice's duplicate).
 * Quoting a machine default as if a human typed it fabricates intent, so the
 * banner suppresses them until the popover captures a real reason.
 */
const MACHINE_REASONS = new Set(['Killed via ArmorIQ', 'Killed from AIQ Graph']);

function operatorReason(reason: string | null | undefined): string | null {
  if (!reason || MACHINE_REASONS.has(reason)) return null;
  return reason;
}

function useActiveKill(agentId: string) {
  const orgId = useCurrentOrgId();
  const kills = useAgentKills(orgId);
  const active: AgentKill | null = (kills.data ?? []).find((k) => k.agentId === agentId) ?? null;
  return { orgId, kills, active };
}

/* ── Countdown ───────────────────────────────────────────────────────────── */

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Ticks once per second until zero, then stops its own interval and fires
 * `onElapsed` exactly once. Mount with `key={expiresAt}` so an extended or
 * replaced kill restarts the countdown (and re-arms the one-shot).
 */
function Countdown({
  expiresAt,
  onElapsed,
}: {
  expiresAt: string;
  onElapsed?: () => void;
}): ReactElement {
  const [now, setNow] = useState(() => Date.now());
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    const target = new Date(expiresAt).getTime();
    if (target - Date.now() <= 0) {
      onElapsedRef.current?.();
      return undefined;
    }
    const t = setInterval(() => {
      setNow(Date.now());
      if (target - Date.now() <= 0) {
        clearInterval(t);
        onElapsedRef.current?.();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  return (
    <span className="font-mono font-semibold tabular-nums">
      {formatRemaining(new Date(expiresAt).getTime() - now)}
    </span>
  );
}

/* ── Hold-to-kill mechanics ──────────────────────────────────────────────── */

function useHold(
  requiredMs: number,
  onComplete: () => void,
  /** Read synchronously on every tick; a true value aborts the hold. */
  invalidRef?: React.RefObject<boolean>
) {
  const [progress, setProgress] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Latest completion callback without restarting an in-flight hold.
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  // Page-level listeners live only for the duration of one hold.
  const detach = useRef<(() => void) | null>(null);

  const cancel = (): void => {
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
    detach.current?.();
    detach.current = null;
    setProgress(0);
  };

  const start = (): void => {
    if (timer.current !== null) return;
    const startedAt = Date.now();
    // A hold is only valid while the operator can see it. Alt-tabbing away
    // (window blur) or switching browser tabs (document hidden) fires none of
    // the button's own pointer/key/blur events, and a background interval
    // would otherwise commit on elapsed wall time (pullfrog on #590).
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') cancel();
    };
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', onVisibility);
    detach.current = () => {
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    timer.current = setInterval(() => {
      // Gate completion synchronously: the passive-effect cancel below can
      // lag a dismissal by more than one 40ms tick, and a destructive commit
      // must never win that race (pullfrog review on #559). Same for a
      // document that went hidden between events.
      if (invalidRef?.current || document.visibilityState === 'hidden') {
        cancel();
        return;
      }
      const p = Math.min(1, (Date.now() - startedAt) / requiredMs);
      setProgress(p);
      if (p === 1) {
        cancel();
        completeRef.current();
      }
    }, 40);
  };

  // Clear the interval and any page listeners on unmount.
  useEffect(() => cancel, []);
  return { progress, start, cancel };
}

function HoldToKillButton({
  label,
  requiredMs,
  disabled,
  pending,
  armed,
  onCommit,
}: {
  label: string;
  requiredMs: number;
  disabled: boolean;
  pending: boolean;
  /** False the moment the surface is dismissed; cancels any in-flight hold. */
  armed: boolean;
  onCommit: () => void;
}): ReactElement {
  const inert = disabled || pending || !armed;
  // The ref mirrors `inert` at render time so the interval tick reads the
  // current value without waiting for the passive effect below.
  const inertRef = useRef(inert);
  inertRef.current = inert;

  const { progress, start, cancel } = useHold(requiredMs, onCommit, inertRef);

  // Belt to the ref's braces: also reset the fill promptly on inertness so
  // the UI never shows a frozen partial hold.
  useEffect(() => {
    if (inert) cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cancel is stable per mount
  }, [inert]);

  return (
    <button
      type="button"
      disabled={disabled || pending}
      onPointerDown={(e) => {
        // Primary button only: a right-click opens a context menu whose
        // pointerup never reaches us, which would leave the hold running.
        // isPrimary must equal false to block (not merely be absent): an
        // environment that synthesizes pointerdown without the field (jsdom
        // in CI) would otherwise silently disable the hold everywhere.
        if (e.button !== 0 || e.isPrimary === false || inert) return;
        start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat && !inert) start();
      }}
      onKeyUp={cancel}
      onBlur={cancel}
      className={[
        'border-aq-bad/40 text-aq-bad text-aq-sm relative h-9 w-full touch-none overflow-hidden rounded-lg border bg-transparent font-semibold select-none',
        'focus-visible:outline-aq-bad focus-visible:outline-2 focus-visible:outline-offset-1',
        disabled || pending ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className="bg-aq-bad-soft absolute inset-y-0 left-0"
        style={{ width: `${progress * 100}%` }}
        aria-hidden="true"
      />
      <span className="relative">
        {pending ? 'Killing…' : progress > 0 ? 'Keep holding…' : label}
      </span>
    </button>
  );
}

/* ── Trigger + popover ───────────────────────────────────────────────────── */

export function AgentKillSwitchTrigger({ agentId }: { agentId: string }): ReactElement {
  // Anchor the trigger BUTTON (not a wrapper div): Popover restores focus to
  // its anchor on close, and a plain div silently drops the keyboard user's
  // tab position to <body>. Matches every other Popover call site (PinKebab,
  // OrgSwitcher, NotificationBell).
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dur, setDur] = useState<DurationKey>('300');

  const { orgId, active } = useActiveKill(agentId);
  const killMut = useKillAgent(orgId);

  const requiredMs = dur === 'inf' ? INDEFINITE_HOLD_MS : HOLD_MS;
  const duration = DURATION_BY_KEY[dur];

  // Synchronous mirror of `open`: `commit` consults it at the moment the hold
  // completes, so a dismissal that has not re-rendered yet still blocks the
  // mutation. State alone leaves a window of one render (pullfrog on #559).
  const openRef = useRef(false);

  const close = (): void => {
    openRef.current = false;
    setOpen(false);
    killMut.reset();
  };

  const openPopover = (): void => {
    openRef.current = true;
    setOpen(true);
  };

  const commit = (): void => {
    // Defense in depth: the hold cancels when a kill lands mid-press or the
    // surface is dismissed, but a commit that races either must never fire.
    if (!openRef.current || active !== null || killMut.isPending) return;
    killMut.mutate({ agentId, durationSeconds: duration.seconds }, { onSuccess: close });
  };

  return (
    <>
      <Button
        ref={anchorRef}
        variant="ghost"
        size="sm"
        leading={IconZap}
        onClick={() => (open ? close() : openPopover())}
        disabled={active !== null}
        className="text-aq-bad/70 hover:text-aq-bad hover:bg-aq-bad-soft"
      >
        Kill switch
      </Button>
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement="bottom-end"
        // Clamp to the viewport: Popover does not clamp fixed widths itself,
        // and 360px overflows a 320px phone (pullfrog on #559). Read once per
        // open; the E-stop popover's lifetime is seconds, not resizes.
        width={Math.min(360, (typeof window !== 'undefined' ? window.innerWidth : 360) - 24)}
        ariaLabel="Kill switch"
      >
        <div className="flex flex-col gap-3 p-3">
          <div>
            <div className="text-aq-ink-muted tracking-aq-widest text-aq-caption mb-2 font-bold uppercase">
              Duration
            </div>
            <SegmentedControl
              size="sm"
              value={dur}
              onChange={(next) => setDur(next as DurationKey)}
              options={SEG_OPTIONS}
              ariaLabel="Kill duration"
            />
          </div>
          <HoldToKillButton
            label={
              dur === 'inf' ? 'Hold to kill until lifted' : `Hold to kill for ${duration.label}`
            }
            requiredMs={requiredMs}
            disabled={active !== null}
            pending={killMut.isPending}
            armed={open}
            onCommit={commit}
          />
          {killMut.isError ? (
            <p className="text-aq-bad text-aq-xs m-0" role="alert">
              Couldn&apos;t kill the agent. Try again.
            </p>
          ) : (
            <p className="text-aq-ink-muted text-aq-caption m-0">
              Blocks every tool call at IAP verify. Release early to cancel; lift it any time.
            </p>
          )}
        </div>
      </Popover>
    </>
  );
}

/* ── Killed banner ───────────────────────────────────────────────────────── */

export function AgentKillBanner({ agentId }: { agentId: string }): ReactElement | null {
  const queryClient = useQueryClient();
  const { orgId, kills, active } = useActiveKill(agentId);
  const unkillMut = useUnkillAgent(orgId);

  if (active === null) {
    // "Unknown" must not read as "not killed": if the kills query failed, say
    // so. (While loading we stay quiet; a flash of warning on every mount
    // would cry wolf.)
    if (kills.isError) {
      return (
        <div
          className="border-aq-warn/40 bg-aq-warn-soft text-aq-ink-soft text-aq-xs flex items-center gap-2 rounded-lg border px-3 py-2"
          role="alert"
        >
          <IconAlert size={14} stroke={2} className="text-aq-warn" />
          Couldn&apos;t confirm this agent&apos;s kill status. It may still be blocked at IAP
          verify.
        </div>
      );
    }
    return null;
  }

  return (
    <div className="border-aq-bad/30 bg-aq-bad-soft rounded-xl border p-4" role="alert">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <IconAlert size={15} stroke={2} className="text-aq-bad" />
            <span className="text-aq-bad text-aq-sm font-bold">Agent killed</span>
            {active.expiresAt ? (
              <span className="text-aq-ink-soft text-aq-sm inline-flex items-center gap-1.5">
                <IconClock size={13} stroke={1.8} />
                lifts in{' '}
                <Countdown
                  key={active.expiresAt}
                  expiresAt={active.expiresAt}
                  onElapsed={() =>
                    void queryClient.invalidateQueries({ queryKey: killKeys.all(orgId) })
                  }
                />
              </span>
            ) : (
              <span className="text-aq-ink-soft text-aq-sm">until someone lifts it</span>
            )}
          </div>
          <p className="text-aq-ink-soft text-aq-xs mt-1.5 mb-0 leading-relaxed">
            Every tool call from this agent is blocked at IAP verify
            {active.createdBy ? (
              <>
                . Killed by <b className="text-aq-ink">{active.createdBy}</b>
              </>
            ) : null}
            {operatorReason(active.reason) ? (
              <> &middot; &ldquo;{operatorReason(active.reason)}&rdquo;</>
            ) : null}
            .
          </p>
          {unkillMut.isError ? (
            <p className="text-aq-bad text-aq-xs mt-1.5 mb-0" role="alert">
              Couldn&apos;t lift the kill. Try again.
            </p>
          ) : null}
        </div>
        <Button
          variant="secondary"
          size="sm"
          leading={IconShieldCheck}
          loading={unkillMut.isPending}
          onClick={() => unkillMut.mutate(agentId)}
        >
          Lift kill
        </Button>
      </div>
    </div>
  );
}
